import React, { useState, useEffect } from 'react'
import QRCode from 'qrcode'
import { Button, Modal, message } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import styled from 'styled-components'

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const QRCodeImage = styled.img`
  width: 60px;
  height: 60px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`

const QRCodeModal = styled(Modal)`
  .ant-modal-body {
    text-align: center;
    padding: 24px;
  }
`

const QRCodeGenerator = ({ menuId, menuName }) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState(null)
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)

  // Generate QR code data URL
  useEffect(() => {
    const generateQRCode = async () => {
      if (!menuId) return

      try {
        setLoading(true)
        // Create a URL that would link to the allergen grid for this menu
        const allergenGridUrl = `${window.location.origin}/allergen-grid?menu=${menuId}`
        const dataURL = await QRCode.toDataURL(allergenGridUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })
        setQrCodeDataURL(dataURL)
      } catch (error) {
        console.error('Error generating QR code:', error)
        message.error('Failed to generate QR code')
      } finally {
        setLoading(false)
      }
    }

    generateQRCode()
  }, [menuId])

  const handleDownload = () => {
    if (!qrCodeDataURL) return

    try {
      const link = document.createElement('a')
      link.download = `${menuName
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase()}_qr_code.png`
      link.href = qrCodeDataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      message.success('QR code downloaded successfully')
    } catch (error) {
      console.error('Error downloading QR code:', error)
      message.error('Failed to download QR code')
    }
  }

  const handleQRCodeClick = () => {
    setModalVisible(true)
  }

  if (loading) {
    return (
      <div
        style={{
          width: 60,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Loading...
      </div>
    )
  }

  if (!qrCodeDataURL) {
    return null
  }

  return (
    <QRCodeContainer>
      <QRCodeImage
        src={qrCodeDataURL}
        alt={`QR Code for ${menuName}`}
        onClick={handleQRCodeClick}
        title='Click to view full size QR code'
      />
      <Button
        type='primary'
        onClick={handleDownload}
        disabled={!qrCodeDataURL}
        style={{ width: '100%' }}
      >
        <DownloadOutlined /> Download QR Code
      </Button>

      <QRCodeModal
        title={`QR Code - ${menuName}`}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button
            key='download'
            type='primary'
            icon={<DownloadOutlined />}
            onClick={handleDownload}
          >
            Download PNG
          </Button>,
          <Button key='close' onClick={() => setModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <img
            src={qrCodeDataURL}
            alt={`QR Code for ${menuName}`}
            style={{ width: '100%', maxWidth: 300, height: 'auto' }}
          />
        </div>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
          Scan this QR code to view the {menuName} allergen grid
        </p>
      </QRCodeModal>
    </QRCodeContainer>
  )
}

export default QRCodeGenerator
